import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { ColorService } from '@app/shared/services/color.service';
import { AuthUser } from '@app/account/account-api.model';
import { AuthService } from '@app/shared/services/auth.service';

interface BuildYourProfileSection {
  id: string;
  copy: string;
  progress: number;
}

interface BuildYourProfileConfigContent {
  complete: {
    text: string;
    params: { [countId: string]: number };
  };
  incomplete: {
    text: string;
    params: { [countId: string]: number };
  };
  progress: number;
  count: number;
}

interface BuildYourProfileConfig {
  [id: string]: BuildYourProfileConfigContent;
}

@Component({
  selector: 'dgx-build-your-profile',
  templateUrl: './build-your-profile.component.html',
  styleUrls: ['./build-your-profile.component.scss'],
})
export class BuildYourProfileComponent implements OnInit, OnChanges {
  // Bindings
  @Input() public hasParsedResume: boolean;

  @Input() public skillsCount: number = 0;
  @Input() public skillRatingsCount: number = 0;
  @Input() public focusSkillsCount: number = 0;
  @Input() public experiencesCount: number = 0;
  @Input() public achievementsCount: number = 0;
  @Input() public learningsCount: number = 0;
  @Input() public ownerKey: number;

  // Local
  public i18n = this.translate.instant([
    'Core_Completion_Progress',
    'dgProfileOverview_BuildProfileSectionCompleted',
    'dgProfileOverview_BuildProfileTitle',
    'dgProfileOverview_BuildProfileSubTitle',
  ]);
  public config: BuildYourProfileSection[] = [];
  public hasResumeParsing: boolean;
  public completedImg = '/content/img/circle-check-green.svg';

  public progressCircleInProgress = this.ColorSvc.getColor('pending');
  public progressCircleTrailColor = this.ColorSvc.getColor('ebony-a08');

  public authUser: AuthUser;

  private skillsCompleteLimit: number = 10;
  private skillRatingsCompleteLimit: number = 1;
  private focusSkillsCompleteLimit: number = 4;
  private experiencesCompleteLimit: number = 1;
  private achievementsCompleteLimit: number = 1;
  private learningsCompleteLimit: number = 10;

  private skillsPercent: number = 1;
  private focusSkillsPercent: number = 1;
  private skillRatingsPercent: number = 1;
  private experiencesPercent: number = 1;
  private achievementsPercent: number = 1;
  private learningsPercent: number = 1;

  constructor(
    private translate: TranslateService,
    private ColorSvc: ColorService,
    private authService: AuthService
  ) {}

  public ngOnInit(): void {
    this.authUser = this.authService?.authUser;
    const disableResumeParsing =
      this.authUser?.disableResumeParsingInOnboarding;
    this.hasResumeParsing = !disableResumeParsing;
    this.getConfig();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.getConfig();
  }

  public getConfig(): void {
    this.calculatePercentages();
    const baseConfig = this.getBaseConfig();
    this.config = Object.keys(baseConfig).reduce((allConfig, key) => {
      const copy = this.getTranslation(baseConfig[key]);
      allConfig.push({
        copy,
        progress: baseConfig[key].progress,
        id: key,
      });
      return allConfig;
    }, []);
  }

  public trackByConfigId(
    index: number,
    section: BuildYourProfileSection
  ): string {
    return section.id;
  }

  private getBaseConfig(): BuildYourProfileConfig {
    return {
      skills: {
        complete: {
          text: 'dgProfileOverview_BuildProfileSkillsComplete',
          params: { skillsCount: this.skillsCount },
        },
        incomplete: {
          text: 'dgProfileOverview_BuildProfileSkillsIncomplete',
          params: { skillsLimit: this.skillsCompleteLimit },
        },
        progress: this.skillsPercent,
        count: this.skillsCount,
      },
      focusSkills: {
        complete: {
          text: 'dgProfileOverview_BuildProfileFocusSkillsComplete',
          params: { focusSkillsCount: this.focusSkillsCount },
        },
        incomplete: {
          text: 'dgProfileOverview_BuildProfileFocusSkillsIncomplete',
          params: { focusSkillsLimit: this.focusSkillsCompleteLimit },
        },
        progress: this.focusSkillsPercent,
        count: this.focusSkillsCount,
      },
      skillRatings: {
        complete: {
          text: 'dgProfileOverview_BuildProfileSkillRatingsComplete',
          params: { skillRatingsCount: this.skillRatingsCount },
        },
        incomplete: {
          text: 'dgProfileOverview_BuildProfileSkillRatingsIncomplete',
          params: { skillRatingsLimit: this.skillRatingsCompleteLimit },
        },
        progress: this.skillRatingsPercent,
        count: this.skillRatingsCount,
      },
      experiences: {
        complete: {
          text: 'dgProfileOverview_BuildProfileExperiencesComplete',
          params: { experiencesCount: this.experiencesCount },
        },
        incomplete: {
          text: 'dgProfileOverview_BuildProfileExperiencesIncomplete',
          params: { experiencesLimit: this.experiencesCompleteLimit },
        },
        progress: this.experiencesPercent,
        count: this.experiencesCount,
      },
      achievements: {
        complete: {
          text: 'dgProfileOverview_BuildProfileAchievementsComplete',
          params: { achievementsCount: this.achievementsCount },
        },
        incomplete: {
          text: 'dgProfileOverview_BuildProfileAchievementsIncomplete',
          params: { achievementsLimit: this.achievementsCompleteLimit },
        },
        progress: this.achievementsPercent,
        count: this.achievementsCount,
      },
      learnings: {
        complete: {
          text: 'dgProfileOverview_BuildProfileLearningsComplete',
          params: { learningsCount: this.learningsCount },
        },
        incomplete: {
          text: 'dgProfileOverview_BuildProfileLearningsIncomplete',
          params: { learningsLimit: this.learningsCompleteLimit },
        },
        progress: this.learningsPercent,
        count: this.learningsCount,
      },
    };
  }

  private getTranslation(config: BuildYourProfileConfigContent): string {
    if (config.progress === 100) {
      const completeConfig = config.complete;
      const translationKey =
        config.count === 1
          ? `${completeConfig.text}_Single`
          : `${completeConfig.text}_Plural`;
      return this.translate.instant(translationKey, completeConfig.params);
    } else {
      return this.translate.instant(
        config.incomplete.text,
        config.incomplete.params
      );
    }
  }

  private calculatePercentages = (): void => {
    const calculate = (count, limit) => Math.min((count / limit) * 100, 100);
    this.skillsPercent = calculate(this.skillsCount, this.skillsCompleteLimit);
    this.focusSkillsPercent = calculate(
      this.focusSkillsCount,
      this.focusSkillsCompleteLimit
    );
    this.skillRatingsPercent = calculate(
      this.skillRatingsCount,
      this.skillRatingsCompleteLimit
    );
    this.experiencesPercent = calculate(
      this.experiencesCount,
      this.experiencesCompleteLimit
    );
    this.learningsPercent = calculate(
      this.learningsCount,
      this.learningsCompleteLimit
    );
    this.achievementsPercent = calculate(
      this.achievementsCount,
      this.achievementsCompleteLimit
    );
  };
}
